import React from "react";

import { useApolloClient } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_REASON, { UPDATE_REASON } from "../../provider/Reasons/mutation";
import { REASONS } from "../../provider/Reasons/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";
import { _reasonType, _reasonPage } from "../../utilities/constants";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const toast = React.useRef<any>(null);
  const dt = React.useRef<any>(null);

  const [records, setRecords] = React.useState<iReason[]>([]);
  const [record, setRecord] = React.useState<iReason>();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
  const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = React.useState<any>(null);

  let emptyRecord: iReason = {
    name: "",
    page: "CANCELLATION",
    type: "DRIVER",
    is_actived: true,
  };

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    apollo
      .query({
        query: REASONS,
        variables: {
          // limit: perPage,
          // filter: filter,
        },
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.reasons;

        setRecords(data);
        setLoading(false);
      })
      .catch(error => {
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
      });
  };

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (!record?.name) {
      return;
    }

    const _record: any = { ...record };

    delete _record.__typename;

    setLoading(true);
    let mutation = CREATE_REASON;

    if (record?.id) {
      _record.id = parseInt(`${_record.id}`);
      mutation = UPDATE_REASON;
    }

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iReason) => {
    const _record: iReason = { ...record };

    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_REASON,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const onRoleChange = (e: any, key: string) => {
    let _record: any = { ...record };
    _record[key] = e.value;
    setRecord(_record);
  };

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    let _record: any = { ...record };
    _record[name] = val;

    setRecord(_record);
  };

  const leftToolbarTemplate = () => {
    return (
      <React.Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
        {/* <Button
          label="Delete"
          icon="pi pi-trash"
          className="p-button-danger mb-2"
          onClick={confirmDeleteSelected}
          disabled={!selectedRecords || !selectedRecords.length}
        /> */}
      </React.Fragment>
    );
  };

  const codeBodyTemplate = (rowData: iReason) => {
    return (
      <>
        <span className="p-column-title">ID</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iReason) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const typeBodyTemplate = (rowData: iReason) => {
    return (
      <>
        <span className="p-column-title">Tipo</span>
        {_reasonType(rowData.type)}
      </>
    );
  };

  const pageBodyTemplate = (rowData: iReason) => {
    return (
      <>
        <span className="p-column-title">Página</span>
        {_reasonPage(rowData.page)}
      </>
    );
  };

  const statusBodyTemplate = (rowData: iReason) => {
    return (
      <>
        <span className="p-column-title">Situação</span>
        <span className={`product-badge status-${!rowData.is_actived ? "disabled" : "actived"}`}>{rowData.is_actived ? "Ativo" : "Inativo"}</span>
      </>
    );
  };

  const actionBodyTemplate = (rowData: iReason) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando Motivos & Assuntos..." : "Motivos & Assuntos"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  React.useEffect(() => {
    loadRecords(true);
  }, []);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />
          <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

          <DataTable
            ref={dt}
            value={records}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll">
            <Column field="id" header="ID" sortable body={codeBodyTemplate} headerStyle={{}}></Column>
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
            <Column field="type" header="Tipo" sortable body={typeBodyTemplate} headerStyle={{}}></Column>
            <Column field="page" header="Página" sortable body={pageBodyTemplate} headerStyle={{}}></Column>
            <Column field="is_actived" header="Situação" sortable body={statusBodyTemplate} headerStyle={{}}></Column>

            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate}></Column>
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Motivo" : "Novo Motivo"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            <div className="flex flex-column md:flex-row flex-wrap">
              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome/Assunto</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome/Assunto é obrigatório.</small>}
              </div>
            </div>
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Tipo</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="type1"
                      name="type"
                      value={"DRIVER"}
                      onChange={e => onRoleChange(e, "type")}
                      checked={record?.type === "DRIVER" ? true : false}
                    />
                    <label htmlFor="type1">Motorista</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="type2"
                      name="type"
                      value={"PASSENGER"}
                      onChange={e => onRoleChange(e, "type")}
                      checked={record?.type === "PASSENGER" ? true : false}
                    />
                    <label htmlFor="type2">Passageiro</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Usar em qual página?</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="page1"
                      name="type"
                      value={"CANCELLATION"}
                      onChange={e => onRoleChange(e, "page")}
                      checked={record?.page === "CANCELLATION" ? true : false}
                    />
                    <label htmlFor="page1">Cancelamento</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="page2"
                      name="page"
                      value={"SUPPORT"}
                      onChange={e => onRoleChange(e, "page")}
                      checked={record?.page === "SUPPORT" ? true : false}
                    />
                    <label htmlFor="page2">Suporte</label>
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Situação</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="role1"
                      name="is_actived"
                      value={true}
                      onChange={e => onRoleChange(e, "is_actived")}
                      checked={record?.is_actived}
                    />
                    <label htmlFor="role1">Ativo</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton
                      inputId="role2"
                      name="is_actived"
                      value={false}
                      onChange={e => onRoleChange(e, "is_actived")}
                      checked={!record?.is_actived}
                    />
                    <label htmlFor="role2">Inativo</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
